import React, { FC, useEffect, useState } from 'react';

import {
  Box,
  CircularProgress,
  makeStyles,
  Typography
} from '@material-ui/core';

import { useObserver } from 'mobx-react-lite';

import CurrencyFormat from '../../../Core/CurrencyFormat/CurrencyFormat';
import { useOrdering } from '../../../../stores/OrderingStore';
import { DeliveryQuote } from 'kiolyn-api';
import moment from 'moment';
import { ViewState } from '../../../../domain/models/ViewState';
import { roundM } from '../../../../domain/Money';

const useStyles = makeStyles(theme => ({
  ok: {},
  loading: {
    opacity: 0.5
  }
}));

interface EstimatedDeliveryInfoProps {
  asap?: boolean;
  destination?: string;
  orderValue?: number;
  deliveryIsoTime?: string;
  onQuoteChange?: (quote?: DeliveryQuote) => void;
}

const EstimatedDeliveryInfo: FC<EstimatedDeliveryInfoProps> = ({
  asap,
  destination,
  deliveryIsoTime,
  orderValue,
  onQuoteChange = id => {}
}) => {
  const classes = useStyles();
  const orderingStore = useOrdering();

  const [quote, setQuote] = useState<DeliveryQuote>();
  const [error, setError] = useState<string>();

  const [deliveryTime, setDeliveryTime] = useState('');
  const [deliveryFee, setDeliveryFee] = useState(0);

  useEffect(() => {
    if (quote) {
      const fee = roundM((quote.fee ?? 0) / 100);
      setDeliveryTime(moment(quote.dropoff_eta).format('h:mm A'));
      setDeliveryFee(fee);
      orderingStore.setEstimatedDeliveryFeeAndDuration(
        fee,
        quote.dropoff_eta ?? '',
        quote.id ?? ''
      );
    } else {
      setDeliveryTime('');
      setDeliveryFee(0);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderingStore, quote, setDeliveryTime, setDeliveryFee]);

  const handleQuoteChange = (q?: DeliveryQuote) => {
    setQuote(q);
    onQuoteChange(q);
  };

  useEffect(() => {
    const createDeliveryQuote = async () => {
      handleQuoteChange(undefined);
      setError(undefined);
      if (
        destination !== undefined &&
        destination !== '' &&
        (asap === true || deliveryIsoTime)
      ) {
        try {
          const res = await orderingStore.createDeliveryQuote(
            destination,
            deliveryIsoTime,
            orderValue
          );
          handleQuoteChange(res);
        } catch (e) {
          setError(e.toString());
        }
      }
    };
    createDeliveryQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderingStore, destination, asap, deliveryIsoTime]);

  return useObserver(() =>
    orderingStore.deliveryQuoteState === ViewState.LOADING ? (
      <CircularProgress />
    ) : (
      <Box className={classes.ok}>
        {error && error !== '' && (
          <Typography color="secondary" display="block">
            {error}
          </Typography>
        )}
        <Typography variant="h6" display="block">
          <CurrencyFormat
            amount={deliveryFee}
            prefix={'Estimated delivery fee: '}
          />
        </Typography>
        <Typography variant="h6" display="block">
          {`Estimated delivery time: ${deliveryTime}`}
        </Typography>
      </Box>
    )
  );
};

export default EstimatedDeliveryInfo;
